import * as Popover from '@radix-ui/react-popover';
import * as Tooltip from '~/components/ui/Tooltip';
import { useMemo, useState } from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shadcn/components/ui/command';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shadcn/components/ui/table';
import { useGetLogs } from '@shadcn/hooks/use-logs';
import { LogsTableName, genDefaultQuery } from '@shadcn/lib/logs';
import { Alert } from '~/components/ui/Alert';
import { Button } from '~/components/ui/Button';
import { Skeleton } from '~/components/ui/Skeleton';
import { classNames as cn } from '~/utils/classNames';
import { usePaginatedData } from '~/lib/hooks/usePaginatedData';
import { Pagination } from '~/components/ui/Pagination';
import { SortIndicator } from '~/components/ui/SortIndicator';

const logTypes = [
  {
    value: LogsTableName.FN_EDGE,
    label: 'Server Function Logs',
    description: 'Server function execution logs with request/response data',
  },
  {
    value: LogsTableName.AUTH,
    label: 'Authentication Logs',
    description: 'User authentication events and security logs',
  },
  {
    value: LogsTableName.POSTGRES,
    label: 'PostgreSQL Logs',
    description: 'Database queries, errors, and performance metrics',
  },
  {
    value: LogsTableName.REALTIME,
    label: 'Realtime Logs',
    description: 'WebSocket connections and realtime subscriptions',
  },
  {
    value: LogsTableName.STORAGE,
    label: 'Storage Logs',
    description: 'File uploads, downloads, and storage operations',
  },
  {
    value: LogsTableName.PG_CRON,
    label: 'Cron Job Logs',
    description: 'Scheduled job executions and cron task logs',
  },
  {
    value: LogsTableName.EDGE,
    label: 'Server Logs',
    description: 'HTTP requests and responses from Server Functions',
  },

  {
    value: LogsTableName.FUNCTIONS,
    label: 'Function Logs',
    description: 'Serverless function execution logs and events',
  },
  {
    value: LogsTableName.POSTGREST,
    label: 'PostgREST Logs',
    description: 'API requests to your database through PostgREST',
  },
  {
    value: LogsTableName.SUPAVISOR,
    label: 'Supavisor Logs',
    description: 'Connection pooling and database proxy logs',
  },
  {
    value: LogsTableName.PGBOUNCER,
    label: 'PgBouncer Logs',
    description: 'Legacy connection pooling logs',
  },
  {
    value: LogsTableName.WAREHOUSE,
    label: 'Warehouse Logs',
    description: 'Data warehouse operations and analytics',
  },
  {
    value: LogsTableName.PG_UPGRADE,
    label: 'PostgreSQL Upgrade Logs',
    description: 'Database upgrade processes and migration logs',
  },
];

function LogsTypeSelector({
  activeTable,
  setActiveTable,
}: {
  activeTable: LogsTableName;
  setActiveTable: (table: LogsTableName) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedLogType = logTypes.find((type) => type.value === activeTable);

  return (
    <Popover.Root open={open} onOpenChange={setOpen} modal={true}>
      <Popover.Trigger asChild>
        <Button
          truncate={false}
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className="flex w-100 justify-between bg-bolt-ds-surface hover:bg-bolt-ds-surfaceHighlight"
        >
          {selectedLogType ? selectedLogType.label : 'Select log type...'}
          <div className="i-bolt:chevron-down ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="w-100 p-0 bg-bolt-ds-surface z-max rounded-md border border-bolt-ds-borderOutline"
          align="end"
        >
          <Command className="bg-bolt-ds-surface">
            <CommandInput placeholder="Search log types..." className="h-9" />
            <CommandList
              className="max-h-60 overflow-y-auto bg-bolt-ds-surface"
              onWheel={(e) => {
                e.stopPropagation();
              }}
              style={{ overscrollBehavior: 'contain' }}
            >
              <CommandEmpty>No log type found.</CommandEmpty>
              <CommandGroup>
                {logTypes.map((logType) => (
                  <CommandItem
                    key={logType.value}
                    value={logType.value}
                    onSelect={(currentValue) => {
                      setActiveTable(currentValue as LogsTableName);
                      setOpen(false);
                    }}
                    className="flex items-center gap-2 p-3"
                  >
                    <div className="flex-1">
                      <div className="text-xs mb-1 font-medium leading-none">{logType.label}</div>
                      <div className="text-xs text-bolt-ds-textSecondary leading-snug">{logType.description}</div>
                    </div>
                    <div
                      className={cn(
                        'i-heroicons:check-solid size-4 mt-0.5',
                        activeTable === logType.value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const DEFAULT_SORT_FIELD = 'timestamp';
const DEFAULT_SORT_DIRECTION: 'ASC' | 'DESC' = 'DESC';
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_LOGS_LIMIT = 100;

export function LogsManager() {
  const [activeTable, setActiveTable] = useState<LogsTableName>(LogsTableName.FN_EDGE);

  const sql = useMemo(
    () => genDefaultQuery(activeTable, DEFAULT_LOGS_LIMIT, DEFAULT_SORT_FIELD, DEFAULT_SORT_DIRECTION),
    [activeTable],
  );

  const { data, isLoading, error } = useGetLogs({ sql });
  const list = useMemo(() => data?.result ?? [], [data]);

  const {
    data: paginatedLogs,
    page,
    onPageChange,
    totalPages,
  } = usePaginatedData(list, {
    pageSize: DEFAULT_PAGE_SIZE,
  });

  return (
    <>
      <div className="p-6 pt-4 lg:p-8 lg:pb-6 flex flex-col gap-2">
        <h1 className="text-base lg:text-xl font-semibold">Logs</h1>
        <p className="hidden lg:block text-sm lg:text-base text-bolt-ds-textSecondary mb-4">
          Debug errors and track activity in your app. Showing activity from the past hour only.
        </p>
        <LogsTypeSelector activeTable={activeTable} setActiveTable={setActiveTable} />
      </div>

      {isLoading && (
        <div className="space-y-2 mx-8">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}
      {!isLoading && error && (
        <div className="mx-6 lg:mx-8">
          <Alert type="danger" title="Error fetching logs">
            {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
          </Alert>
        </div>
      )}
      {!isLoading && paginatedLogs.length > 0 && (
        <div className="flex flex-col gap-4 mx-2 lg:mx-8">
          <Pagination
            page={page}
            allowPageInput
            descriptionNode={`Showing up to ${DEFAULT_LOGS_LIMIT} records`}
            pageSize={DEFAULT_PAGE_SIZE}
            totalItems={list.length}
            totalPages={totalPages}
            onPageChange={onPageChange}
            className="rounded-b-[0px] mb-[-16px]"
          />
          <Table className="border border-bolt-ds-borderOutline rounded-md">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {Object.keys(paginatedLogs[0] as object).map((key, idx, arr) => (
                  <TableHead
                    key={key}
                    className={
                      (idx === 0 ? 'first:pl-6 lg:first:pl-8 ' : '') +
                      (idx === arr.length - 1 ? 'last:pr-6 lg:last:pr-8 ' : '')
                    }
                  >
                    <div className="flex items-center gap-1">
                      <span>{key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</span>
                      <SortIndicator
                        activeField={key}
                        sortField={DEFAULT_SORT_FIELD}
                        sortDirection={DEFAULT_SORT_DIRECTION}
                      />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(paginatedLogs as any[]).map((log, index) => (
                <TableRow key={log.id || index} className="group hover:bg-bolt-ds-surfaceThree/50 relative">
                  {Object.keys(paginatedLogs[0] ?? []).map((key) => {
                    const value = log[key];

                    const formattedValue = (() => {
                      if (key === 'timestamp' && typeof value === 'number') {
                        return new Date(value / 1000).toLocaleString();
                      }

                      if (value === null) {
                        return 'NULL';
                      }

                      return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
                    })();

                    return (
                      <TableCell
                        key={key}
                        className="first:pl-6 lg:first:pl-8 last:pr-6 lg:last:pr-8 text-xs text-bolt-ds-textPrimary group-hover:text-bolt-ds-textPrimary min-w-[8rem]"
                      >
                        <Tooltip.Root delayDuration={300}>
                          <Tooltip.Trigger asChild>
                            <div className="text-xs font-mono w-fit max-w-96 truncate cursor-default">
                              {formattedValue}
                            </div>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content sideOffset={4} align="start" side="top">
                              <div className="w-96 max-h-96 p-3 z-max">
                                <pre className="text-xs font-mono whitespace-pre-wrap break-words">
                                  {formattedValue}
                                </pre>
                              </div>
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {!isLoading && !error && paginatedLogs.length === 0 && (
        <div className="mx-8 text-bolt-ds-textTertiary">
          No {logTypes.find((type) => type.value === activeTable)?.label ?? 'logs'} available yet.
        </div>
      )}
    </>
  );
}
